import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, Building2, Landmark, ChevronDown, Check, ArrowRight,
  Briefcase, CreditCard, Globe, Shield, Zap, TrendingUp,
  Store, Calculator, Plane, Award, BookOpen, Heart,
  Rocket, Mic, ExternalLink, X, Play, Camera
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `/api/win2026`;
const UPLOADS_URL = `/api/uploads`;

// Brand colors
const colors = {
  aqua: "#008B9B",
  aquaDark: "#006064",
  aquaLight: "#E0F7FA",
  gold: "#D4AF37",
  goldLight: "#FDE68A",
  goldDark: "#B4941F",
};

// Logos
const LOGOS = {
  win: `${UPLOADS_URL}/win-logo.png`,
  sag: `${UPLOADS_URL}/sag-logo.png`,
  tdc: `${UPLOADS_URL}/tdc-logo.png`
};

// Venue images
const VENUE_IMAGES = {
  ballroom: `${UPLOADS_URL}/bahamar-ballroom.jpg`,
  entrance: `${UPLOADS_URL}/bahamar-entrance.jpg`,
  aerial: `${UPLOADS_URL}/bahamar-aerial.jpg`,
};

// W.I.N. Summit Event Gallery — fetched dynamically from API

// Ticket Links
const TICKET_LINKS = {
  vendors: "https://www.winsummitbahamas.com/event-details/w-i-n-2026-vendor-speakers",
  established: "https://www.winsummitbahamas.com/event-details/w-i-n-2026-established-entrepreneurs",
  startup: "https://www.winsummitbahamas.com/event-details/w-i-n-2026-start-up-entrepreneurs",
};

// SAG Services
const SAG_SERVICES = [
  { icon: Store, text: "Online Storefront Setup (same-day activation)" },
  { icon: CreditCard, text: "POS & Card Payment Integration" },
  { icon: Zap, text: "Temporary Digital Collection Solutions" },
  { icon: Landmark, text: "Business Banking Assistance" },
  { icon: Globe, text: "Direct Bank Integrations" },
  { icon: Briefcase, text: "Business License Assistance" },
  { icon: TrendingUp, text: "AI & Automation Tools" },
  { icon: Calculator, text: "Financial Structuring Guidance" },
  { icon: Building2, text: "Property & HOA Management Systems" },
  { icon: BookOpen, text: "Bookkeeping & Financial Structure" },
  { icon: Plane, text: "Tourism Booking Platforms" },
  { icon: Award, text: "Export & International Sales Enablement" },
  { icon: Shield, text: "Compliance & Licensing Support" },
  { icon: Heart, text: "International Payment Access" },
];

export default function Win2026Page() {
  const [activeTab, setActiveTab] = useState("entrepreneurs");
  const [eventSettings, setEventSettings] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const registerRef = useRef(null);
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API.replace('/win2026', '/win2026')}/settings`);
        setEventSettings(res.data);
      } catch (error) {
        console.error("Failed to load settings:", error);
      }
    };
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        const items = res.data || [];
        setGalleryPhotos(items.filter(i => i.type === "photo").map(i => ({ src: i.url, alt: i.caption, featured: i.featured })));
        setGalleryVideos(items.filter(i => i.type === "video").map(i => ({ src: i.url, alt: i.caption })));
      } catch (error) {
        console.error("Failed to load gallery:", error);
      }
    };
    fetchSettings();
    fetchGallery();
  }, []);
  
  const scrollToRegister = () => {
    registerRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  // Use dynamic settings or defaults
  const dateDisplay = eventSettings?.date_display || "April 9, 2026";
  const hostName = eventSettings?.host_name || "Tourism Development Corporation";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGOS.sag} alt="SAG" className="h-10" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-gray-600 hover:text-[#008B9B] transition-colors">Services</a>
            <a href="#register" className="text-gray-600 hover:text-[#008B9B] transition-colors">Register</a>
            <Link to="/admin/win-2026" className="text-gray-400 hover:text-gray-600 transition-colors text-sm">Admin</Link>
          </div>
          <Button 
            onClick={scrollToRegister}
            className="bg-[#008B9B] hover:bg-[#006064] text-white"
            data-testid="nav-register-btn"
          >
            Register Now
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E0F7FA] via-white to-[#FDE68A]/20" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23008B9B' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src={LOGOS.win} 
              alt="W.I.N Entrepreneurial Summit 2026" 
              className="mx-auto h-48 md:h-64 mb-8"
            />
            
            <p className="text-lg text-[#D4AF37] font-semibold italic mb-4">Where Ideas Network</p>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-gray-900">From Idea to </span>
              <span className="text-[#D4AF37]">Activation.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-2xl mx-auto">
              Empowering Bahamian Entrepreneurs
            </p>
            
            <p className="inline-block px-5 py-2 bg-[#008B9B] text-white font-bold text-sm uppercase tracking-wider rounded-full mb-8">
              Free Event &bull; April 9, 2026 &bull; Baha Mar, Nassau, The Bahamas
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={scrollToRegister}
                size="lg"
                className="bg-[#008B9B] hover:bg-[#006064] text-white px-8 py-6 text-lg rounded-full"
                data-testid="hero-entrepreneur-btn"
              >
                <Users className="w-5 h-5 mr-2" />
                Register as Entrepreneur
              </Button>
              <Button 
                onClick={scrollToRegister}
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#B4941F] text-white px-8 py-6 text-lg rounded-full"
                data-testid="hero-exhibitor-btn"
              >
                <Building2 className="w-5 h-5 mr-2" />
                Register as Exhibitor
              </Button>
              <Button 
                onClick={scrollToRegister}
                size="lg"
                variant="outline"
                className="border-[#008B9B] text-[#008B9B] hover:bg-[#008B9B] hover:text-white px-8 py-6 text-lg rounded-full"
                data-testid="hero-institution-btn"
              >
                <Landmark className="w-5 h-5 mr-2" />
                Financial Institution
              </Button>
            </div>
            
            {/* Partner Badge */}
            <div className="flex items-center justify-center gap-3 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 mx-auto w-fit shadow-lg">
              <img src={LOGOS.sag} alt="SAG" className="h-8" />
              <span className="text-sm text-gray-600">Digital Activation & Financial Infrastructure Partner</span>
            </div>
          </motion.div>
          
          {/* Scroll indicator */}
          <motion.div 
            className="absolute bottom-0 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown className="w-8 h-8 text-[#008B9B]" />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="text-[#D4AF37] font-medium tracking-wider uppercase text-sm">What We Bring</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              We Don't Just Talk Business —<br />
              <span className="text-[#008B9B]">We Activate It.</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              At the Summit, Sebastian Alliance Group will provide comprehensive business activation services.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {SAG_SERVICES.map((service, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-[#F8FAFC] to-white border border-gray-100 hover:border-[#008B9B]/30 hover:shadow-md transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-10 h-10 rounded-lg bg-[#008B9B]/10 flex items-center justify-center flex-shrink-0">
                  <service.icon className="w-5 h-5 text-[#008B9B]" />
                </div>
                <span className="text-sm text-gray-700">{service.text}</span>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 mt-12 text-sm max-w-3xl mx-auto">
            We are building the digital and financial infrastructure layer that enables Bahamian entrepreneurs to operate, scale, and compete globally.
          </p>
        </div>
      </section>

      {/* Partner Quote Section */}
      <section className="py-20 bg-gradient-to-br from-[#008B9B] to-[#006064] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <img src={LOGOS.tdc} alt="Tourism Development Corporation" className="h-28 mx-auto mb-8 bg-white p-4 rounded-xl" />
          
          <blockquote className="text-2xl md:text-3xl font-light italic mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            "In support of the Tourism Development Corporation's mission to cultivate Bahamian entrepreneurship, Sebastian Alliance Group provides the execution infrastructure that transforms ideas into operational enterprises."
          </blockquote>
          
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
              We are not replacing government initiatives.
            </div>
            <div className="bg-[#D4AF37] px-4 py-2 rounded-full text-sm font-medium">
              We are enabling execution.
            </div>
          </div>
          
          {/* Bridge visualization */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            {["Entrepreneurs", "Banks", "Financial Aid Providers", "Government Agencies", "International Markets"].map((item, i) => (
              <div key={i} className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg text-sm">
                {item}
              </div>
            ))}
          </div>
          <p className="text-[#D4AF37] mt-6 font-medium">We serve as the activation bridge</p>
        </div>
      </section>

      {/* Venue Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] font-semibold text-sm uppercase tracking-widest mb-3">The Venue</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#1E3A5F]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Baha Mar Resort
            </h2>
            <p className="text-gray-600 text-lg">Nassau, The Bahamas</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl overflow-hidden shadow-lg md:col-span-2 h-72 md:h-96">
              <img src={VENUE_IMAGES.aerial} alt="Baha Mar Resort, Nassau, The Bahamas" className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col gap-4">
              <div className="rounded-xl overflow-hidden shadow-lg h-44 md:h-[calc(50%-8px)]">
                <img src={VENUE_IMAGES.entrance} alt="Baha Mar Convention Centre" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg h-44 md:h-[calc(50%-8px)]">
                <img src={VENUE_IMAGES.ballroom} alt="Baha Mar Grand Ballroom" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Highlights Gallery */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#008B9B]/10 rounded-full mb-4">
              <Camera className="w-4 h-4 text-[#008B9B]" />
              <span className="text-[#008B9B] font-semibold text-sm uppercase tracking-wider">Event Highlights</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#1E3A5F]" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Energy of <span className="text-[#D4AF37]">W.I.N.</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              A look at the summit experience — connecting entrepreneurs, financial institutions, and government leaders.
            </p>
          </motion.div>
          
          {/* Photo Grid */}
          {galleryPhotos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
            {galleryPhotos.map((photo, index) => (
              <motion.div
                key={index}
                className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                  (photo.featured || index === 0) ? "col-span-2 row-span-2" : ""
                }`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                onClick={() => setLightboxImg(photo.src)}
                data-testid={`win-photo-${index}`}
              >
                <img 
                  src={photo.src} 
                  alt={photo.alt}
                  className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                    (photo.featured || index === 0) ? "h-72 md:h-[420px]" : "h-36 md:h-48"
                  }`}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                    <Camera className="w-5 h-5 text-[#008B9B]" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}

          {/* Videos */}
          {galleryVideos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {galleryVideos.map((video, index) => (
              <motion.div
                key={index}
                className="relative rounded-xl overflow-hidden bg-black"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`win-video-${index}`}
              >
                <video 
                  className="w-full h-56 object-cover"
                  controls
                  preload="metadata"
                  playsInline
                >
                  <source src={video.src} type="video/mp4" />
                </video>
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                  <Play className="w-3 h-3 text-white fill-white" />
                  <span className="text-white text-xs font-medium">{video.alt || `Video ${index + 1}`}</span>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Get Tickets Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A5F] to-[#0F2035] text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[#D4AF37] font-semibold text-sm uppercase tracking-widest mb-3">Secure Your Spot</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get Your <span className="text-[#D4AF37]">Tickets</span>
          </h2>
          <p className="text-white/70 mb-12 text-lg">Choose the ticket that fits your journey</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Startup Entrepreneurs */}
            <a 
              href={TICKET_LINKS.startup}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 hover:border-[#008B9B] rounded-2xl p-8 transition-all hover:bg-white/10 hover:scale-105"
              data-testid="ticket-startup"
            >
              <div className="w-16 h-16 bg-[#008B9B]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Rocket className="w-8 h-8 text-[#008B9B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Start-Up Entrepreneurs</h3>
              <p className="text-white/60 text-sm mb-6">For new businesses and aspiring entrepreneurs ready to launch</p>
              <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#008B9B] hover:bg-[#006064] text-white font-semibold rounded-full transition-colors text-sm">
                Get Tickets <ExternalLink className="w-4 h-4" />
              </span>
            </a>

            {/* Established Business */}
            <a 
              href={TICKET_LINKS.established}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-[#D4AF37]/30 hover:border-[#D4AF37] rounded-2xl p-8 transition-all hover:bg-white/10 hover:scale-105 relative"
              data-testid="ticket-established"
            >
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-white text-xs font-bold rounded-full uppercase">Popular</span>
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Building2 className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Established Entrepreneurs</h3>
              <p className="text-white/60 text-sm mb-6">For existing businesses looking to scale and expand their reach</p>
              <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] hover:bg-[#B4941F] text-white font-semibold rounded-full transition-colors text-sm">
                Get Tickets <ExternalLink className="w-4 h-4" />
              </span>
            </a>

            {/* Vendors / Speakers */}
            <a 
              href={TICKET_LINKS.vendors}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 hover:border-[#006064] rounded-2xl p-8 transition-all hover:bg-white/10 hover:scale-105"
              data-testid="ticket-vendors"
            >
              <div className="w-16 h-16 bg-[#006064]/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Mic className="w-8 h-8 text-[#00ACC1]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Vendors & Speakers</h3>
              <p className="text-white/60 text-sm mb-6">For vendors, exhibitors, and speakers presenting at the summit</p>
              <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#006064] hover:bg-[#004D40] text-white font-semibold rounded-full transition-colors text-sm">
                Get Tickets <ExternalLink className="w-4 h-4" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section id="register" ref={registerRef} className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Register for <span className="text-[#008B9B]">W.I.N 2026</span>
            </h2>
            <p className="text-gray-600">{dateDisplay} | The Bahamas</p>
          </div>
          
          {/* Tabs */}
          <div className="flex justify-center mb-8 px-2">
            <div className="inline-flex bg-white rounded-full p-1 shadow-lg flex-wrap justify-center gap-1">
              <button
                onClick={() => setActiveTab("entrepreneurs")}
                className={`flex items-center gap-1.5 px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition-all text-sm sm:text-base ${
                  activeTab === "entrepreneurs" 
                    ? "bg-[#008B9B] text-white" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid="tab-entrepreneurs"
              >
                <Users className="w-4 h-4" />
                Entrepreneurs
              </button>
              <button
                onClick={() => setActiveTab("exhibitors")}
                className={`flex items-center gap-1.5 px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition-all text-sm sm:text-base ${
                  activeTab === "exhibitors" 
                    ? "bg-[#D4AF37] text-white" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid="tab-exhibitors"
              >
                <Building2 className="w-4 h-4" />
                Exhibitors
              </button>
              <button
                onClick={() => setActiveTab("institutions")}
                className={`flex items-center gap-1.5 px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition-all text-sm sm:text-base ${
                  activeTab === "institutions" 
                    ? "bg-[#006064] text-white" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid="tab-institutions"
              >
                <Landmark className="w-4 h-4" />
                Institutions
              </button>
            </div>
          </div>
          
          {/* Forms */}
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 light-form">
            <AnimatePresence mode="wait">
              {activeTab === "entrepreneurs" && (
                <motion.div
                  key="entrepreneurs"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <EntrepreneurForm />
                </motion.div>
              )}
              {activeTab === "exhibitors" && (
                <motion.div
                  key="exhibitors"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ExhibitorForm />
                </motion.div>
              )}
              {activeTab === "institutions" && (
                <motion.div
                  key="institutions"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <InstitutionForm />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      <AnimatePresence>
        {lightboxImg && (
          <motion.div 
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxImg(null)}
            data-testid="win-lightbox"
          >
            <button 
              className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              onClick={() => setLightboxImg(null)}
              data-testid="win-lightbox-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img 
              src={lightboxImg} 
              alt="W.I.N. Summit" 
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <img src={LOGOS.sag} alt="SAG" className="h-12" />
              <img src={LOGOS.tdc} alt="TDC" className="h-12 bg-white/10 p-2 rounded" />
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">W.I.N Entrepreneurial Summit 2026</p>
              <p className="text-gray-500 text-xs mt-1">
                Hosted by Tourism Development Corporation | Powered by Sebastian Alliance Group
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center">
            <Link to="/" className="text-[#008B9B] hover:text-[#00AEEF] text-sm">
              ← Back to SAG Events Portal
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ===================== ENTREPRENEUR FORM =====================
function EntrepreneurForm() {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    islands: [], industries: [], stages: [], needs: [], days: [], slots: []
  });
  const [formData, setFormData] = useState({
    full_name: "", business_name: "", idea_stage: false,
    email: "", phone: "", island: "", business_stage: "",
    industry_category: "", needs: [], other_needs: "",
    meet_at_summit: false, preferred_meeting_day: "", preferred_time_slot: "", notes: ""
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [islands, industries, stages, needs, days, slots] = await Promise.all([
          axios.get(`${API}/options/islands`),
          axios.get(`${API}/options/industries`),
          axios.get(`${API}/options/business-stages`),
          axios.get(`${API}/options/entrepreneur-needs`),
          axios.get(`${API}/options/meeting-days`),
          axios.get(`${API}/options/time-slots`)
        ]);
        setOptions({
          islands: islands.data.options,
          industries: industries.data.options,
          stages: stages.data.options,
          needs: needs.data.options,
          days: days.data.options,
          slots: slots.data.options
        });
      } catch (error) {
        console.error("Failed to load options:", error);
      }
    };
    fetchOptions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/entrepreneurs`, formData);
      toast.success("Registration successful! We'll be in touch soon.");
      setFormData({
        full_name: "", business_name: "", idea_stage: false,
        email: "", phone: "", island: "", business_stage: "",
        industry_category: "", needs: [], other_needs: "",
        meet_at_summit: false, preferred_meeting_day: "", preferred_time_slot: "", notes: ""
      });
    } catch (error) {
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleNeed = (need) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs.includes(need) 
        ? prev.needs.filter(n => n !== need)
        : [...prev.needs, need]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-[#008B9B]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Ready to Launch?
        </h3>
        <p className="text-gray-600 text-sm mt-2">
          Whether you have an idea, a startup, an existing tourism business, or a scaling enterprise — register here and meet with SAG at the Summit for activation support.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Full Name *</Label>
          <Input 
            value={formData.full_name}
            onChange={(e) => setFormData({...formData, full_name: e.target.value})}
            required
            className="mt-1"
            data-testid="entrepreneur-name"
          />
        </div>
        <div>
          <Label>Business Name (if applicable)</Label>
          <Input 
            value={formData.business_name}
            onChange={(e) => setFormData({...formData, business_name: e.target.value})}
            disabled={formData.idea_stage}
            className="mt-1"
            data-testid="entrepreneur-business"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox 
          checked={formData.idea_stage}
          onCheckedChange={(checked) => setFormData({...formData, idea_stage: checked, business_name: checked ? "" : formData.business_name})}
          data-testid="entrepreneur-idea-stage"
        />
        <Label className="text-sm text-gray-600">I'm at the idea stage (no business name yet)</Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Email *</Label>
          <Input 
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            className="mt-1"
            data-testid="entrepreneur-email"
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input 
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
            className="mt-1"
            data-testid="entrepreneur-phone"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Island *</Label>
          <Select value={formData.island} onValueChange={(v) => setFormData({...formData, island: v})}>
            <SelectTrigger className="mt-1" data-testid="entrepreneur-island">
              <SelectValue placeholder="Select island" />
            </SelectTrigger>
            <SelectContent>
              {options.islands.map(island => (
                <SelectItem key={island} value={island}>{island}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Business Stage *</Label>
          <Select value={formData.business_stage} onValueChange={(v) => setFormData({...formData, business_stage: v})}>
            <SelectTrigger className="mt-1" data-testid="entrepreneur-stage">
              <SelectValue placeholder="Select stage" />
            </SelectTrigger>
            <SelectContent>
              {options.stages.map(stage => (
                <SelectItem key={stage} value={stage}>{stage}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Industry Category *</Label>
        <Select value={formData.industry_category} onValueChange={(v) => setFormData({...formData, industry_category: v})}>
          <SelectTrigger className="mt-1" data-testid="entrepreneur-industry">
            <SelectValue placeholder="Select industry" />
          </SelectTrigger>
          <SelectContent>
            {options.industries.map(industry => (
              <SelectItem key={industry} value={industry}>{industry}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-3 block">What do you need? (Select all that apply)</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {options.needs.map(need => (
            <div key={need} className="flex items-start gap-2">
              <Checkbox 
                checked={formData.needs.includes(need)}
                onCheckedChange={() => toggleNeed(need)}
                className="mt-0.5 shrink-0"
              />
              <span className="text-sm text-gray-700">{need}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 p-4 bg-[#E0F7FA] rounded-lg">
        <Checkbox 
          checked={formData.meet_at_summit}
          onCheckedChange={(checked) => setFormData({...formData, meet_at_summit: checked})}
          data-testid="entrepreneur-meet"
        />
        <Label className="text-[#008B9B] font-medium">I want to meet with SAG at the Summit</Label>
      </div>

      {formData.meet_at_summit && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Preferred Meeting Day</Label>
            <Select value={formData.preferred_meeting_day} onValueChange={(v) => setFormData({...formData, preferred_meeting_day: v})}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                {options.days.map(day => (
                  <SelectItem key={day} value={day}>{day}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Preferred Time Slot</Label>
            <Select value={formData.preferred_time_slot} onValueChange={(v) => setFormData({...formData, preferred_time_slot: v})}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent>
                {options.slots.map(slot => (
                  <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div>
        <Label>Additional Notes</Label>
        <Textarea 
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Anything else we should know?"
          className="mt-1"
          data-testid="entrepreneur-notes"
        />
      </div>

      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-[#008B9B] hover:bg-[#006064] text-white py-6 text-lg"
        data-testid="entrepreneur-submit"
      >
        {loading ? "Submitting..." : "Register as Entrepreneur"}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </form>
  );
}

// ===================== EXHIBITOR FORM =====================
function ExhibitorForm() {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    services: [], interests: [], orgTypes: []
  });
  const [formData, setFormData] = useState({
    company_name: "", rep_name: "", email: "", phone: "",
    organization_type: "", services_offered: [], other_services: "",
    participation_interest: [], website: "", notes: ""
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [services, interests, orgTypes] = await Promise.all([
          axios.get(`${API}/options/exhibitor-services`),
          axios.get(`${API}/options/participation-interests`),
          axios.get(`${API}/options/organization-types`)
        ]);
        setOptions({
          services: services.data.options,
          interests: interests.data.options,
          orgTypes: orgTypes.data.options
        });
      } catch (error) {
        console.error("Failed to load options:", error);
      }
    };
    fetchOptions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/exhibitors`, formData);
      toast.success("Registration successful! We'll be in touch soon.");
      setFormData({
        company_name: "", rep_name: "", email: "", phone: "",
        organization_type: "", services_offered: [], other_services: "",
        participation_interest: [], website: "", notes: ""
      });
    } catch (error) {
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services_offered: prev.services_offered.includes(service) 
        ? prev.services_offered.filter(s => s !== service)
        : [...prev.services_offered, service]
    }));
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      participation_interest: prev.participation_interest.includes(interest) 
        ? prev.participation_interest.filter(i => i !== interest)
        : [...prev.participation_interest, interest]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Become an Exhibitor
        </h3>
        <p className="text-gray-600 text-sm mt-2">
          For companies and organizations wanting to participate in W.I.N Summit 2026.
        </p>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Submissions collected through this portal will be compiled and shared with the appropriate event coordinators and Ministry representatives for review and official approval.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Company Name *</Label>
          <Input 
            value={formData.company_name}
            onChange={(e) => setFormData({...formData, company_name: e.target.value})}
            required
            className="mt-1"
            data-testid="exhibitor-company"
          />
        </div>
        <div>
          <Label>Representative Name *</Label>
          <Input 
            value={formData.rep_name}
            onChange={(e) => setFormData({...formData, rep_name: e.target.value})}
            required
            className="mt-1"
            data-testid="exhibitor-rep"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Email *</Label>
          <Input 
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            className="mt-1"
            data-testid="exhibitor-email"
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input 
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
            className="mt-1"
            data-testid="exhibitor-phone"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Organization Type *</Label>
          <Select value={formData.organization_type} onValueChange={(v) => setFormData({...formData, organization_type: v})}>
            <SelectTrigger className="mt-1" data-testid="exhibitor-org-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {options.orgTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Website</Label>
          <Input 
            value={formData.website}
            onChange={(e) => setFormData({...formData, website: e.target.value})}
            placeholder="https://"
            className="mt-1"
            data-testid="exhibitor-website"
          />
        </div>
      </div>

      <div>
        <Label className="mb-3 block">Services Offered</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {options.services.map(service => (
            <div key={service} className="flex items-start gap-2">
              <Checkbox 
                checked={formData.services_offered.includes(service)}
                onCheckedChange={() => toggleService(service)}
                className="mt-0.5 shrink-0"
              />
              <span className="text-sm text-gray-700">{service}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="mb-3 block">Participation Interest</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.interests.map(interest => (
            <div key={interest} className="flex items-start gap-2">
              <Checkbox 
                checked={formData.participation_interest.includes(interest)}
                onCheckedChange={() => toggleInterest(interest)}
                className="mt-0.5 shrink-0"
              />
              <span className="text-sm text-gray-700">{interest}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>Additional Notes</Label>
        <Textarea 
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Tell us more about your organization..."
          className="mt-1"
          data-testid="exhibitor-notes"
        />
      </div>

      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-[#D4AF37] hover:bg-[#B4941F] text-white py-6 text-lg"
        data-testid="exhibitor-submit"
      >
        {loading ? "Submitting..." : "Register as Exhibitor"}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </form>
  );
}

// ===================== INSTITUTION FORM =====================
function InstitutionForm() {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    types: [], support: []
  });
  const [formData, setFormData] = useState({
    institution_name: "", rep_name: "", email: "", phone: "",
    institution_type: "", support_offered: [], other_support: "",
    want_booth: false, want_meetings: false, notes: ""
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [types, support] = await Promise.all([
          axios.get(`${API}/options/institution-types`),
          axios.get(`${API}/options/institution-support`)
        ]);
        setOptions({
          types: types.data.options,
          support: support.data.options
        });
      } catch (error) {
        console.error("Failed to load options:", error);
      }
    };
    fetchOptions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/institutions`, formData);
      toast.success("Registration successful! We'll be in touch soon.");
      setFormData({
        institution_name: "", rep_name: "", email: "", phone: "",
        institution_type: "", support_offered: [], other_support: "",
        want_booth: false, want_meetings: false, notes: ""
      });
    } catch (error) {
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSupport = (support) => {
    setFormData(prev => ({
      ...prev,
      support_offered: prev.support_offered.includes(support) 
        ? prev.support_offered.filter(s => s !== support)
        : [...prev.support_offered, support]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-[#006064]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Financial Institution Registration
        </h3>
        <p className="text-gray-600 text-sm mt-2">
          For banks, investors, grant providers, and financial service companies.
        </p>
        <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
          Submissions collected through this portal will be compiled and shared with the appropriate event coordinators and Ministry representatives for review and official approval.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Institution Name *</Label>
          <Input 
            value={formData.institution_name}
            onChange={(e) => setFormData({...formData, institution_name: e.target.value})}
            required
            className="mt-1"
            data-testid="institution-name"
          />
        </div>
        <div>
          <Label>Representative Name *</Label>
          <Input 
            value={formData.rep_name}
            onChange={(e) => setFormData({...formData, rep_name: e.target.value})}
            required
            className="mt-1"
            data-testid="institution-rep"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Email *</Label>
          <Input 
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            className="mt-1"
            data-testid="institution-email"
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input 
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
            className="mt-1"
            data-testid="institution-phone"
          />
        </div>
      </div>

      <div>
        <Label>Institution Type *</Label>
        <Select value={formData.institution_type} onValueChange={(v) => setFormData({...formData, institution_type: v})}>
          <SelectTrigger className="mt-1" data-testid="institution-type">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {options.types.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-3 block">Support Offered</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {options.support.map(support => (
            <div key={support} className="flex items-start gap-2">
              <Checkbox 
                checked={formData.support_offered.includes(support)}
                onCheckedChange={() => toggleSupport(support)}
                className="mt-0.5 shrink-0"
              />
              <span className="text-sm text-gray-700">{support}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-2 p-4 bg-teal-50 rounded-lg">
          <Checkbox 
            checked={formData.want_booth}
            onCheckedChange={(checked) => setFormData({...formData, want_booth: checked})}
            data-testid="institution-booth"
          />
          <Label className="text-[#006064]">We want a booth at the Summit</Label>
        </div>
        <div className="flex items-center gap-2 p-4 bg-teal-50 rounded-lg">
          <Checkbox 
            checked={formData.want_meetings}
            onCheckedChange={(checked) => setFormData({...formData, want_meetings: checked})}
            data-testid="institution-meetings"
          />
          <Label className="text-[#006064]">We want to hold meetings with entrepreneurs</Label>
        </div>
      </div>

      <div>
        <Label>Additional Notes</Label>
        <Textarea 
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Tell us more about your institution's offerings..."
          className="mt-1"
          data-testid="institution-notes"
        />
      </div>

      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-[#006064] hover:bg-[#004D4D] text-white py-6 text-lg"
        data-testid="institution-submit"
      >
        {loading ? "Submitting..." : "Register as Financial Institution"}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </form>
  );
}
